import React, { useState } from 'react';
import { AlertTriangleIcon, ChevronDownIcon, ChevronUpIcon, PlusIcon, TrashIcon, CheckCircleIcon, InfoIcon } from 'lucide-react';
export function ValidationFields({
  extractedData,
  onFieldUpdate,
  onLineItemUpdate,
  locale = 'en-US'
}) {
  const [expandedSections, setExpandedSections] = useState({
    header: true,
    vendor: true,
    lineItems: true,
    glCoding: false
  });
  // Toggle section expansion
  const toggleSection = section => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section]
    });
  };
  // Format currency based on locale
  const formatCurrency = amount => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: extractedData.header.currency || 'USD'
    }).format(amount);
  };
  // Get confidence level class
  const getConfidenceClass = confidence => {
    if (confidence >= 0.8) return 'bg-green-100 text-green-800';
    if (confidence >= 0.6) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-5786">
      <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-5787">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-5788">
          Extracted Data
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-5789">
          Review and correct the extracted document data
        </p>
      </div>
      <div className="px-4 py-5 sm:p-6 space-y-6 max-h-[70vh] overflow-y-auto" data-id="element-5790">
        {/* Document Type */}
        <div data-id="element-5791">
          <div className="flex items-center justify-between" data-id="element-5792">
            <label htmlFor="documentType" className="block text-sm font-medium text-gray-700" data-id="element-5793">
              Document Type
            </label>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getConfidenceClass(extractedData.confidence.documentType)}`} data-id="element-5794">
              {Math.round(extractedData.confidence.documentType * 100)}%
            </span>
          </div>
          <select id="documentType" name="documentType" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={extractedData.documentType} onChange={e => onFieldUpdate('documentType', e.target.value)} data-id="element-5795">
            <option data-id="element-5796">Invoice</option>
            <option data-id="element-5797">Credit Note</option>
            <option data-id="element-5798">Purchase Order</option>
            <option data-id="element-5799">Packing Slip</option>
            <option data-id="element-5800">Receipt</option>
          </select>
        </div>
        {/* Header Section */}
        <div data-id="element-5801">
          <div className="flex items-center justify-between cursor-pointer" onClick={() => toggleSection('header')} data-id="element-5802">
            <h4 className="text-sm font-medium text-gray-900" data-id="element-5803">
              Document Header
            </h4>
            {expandedSections.header ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-5804" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-5805" />}
          </div>
          {expandedSections.header && <div className="mt-3 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-5806">
              <div className="sm:col-span-3" data-id="element-5807">
                <div className="flex items-center justify-between" data-id="element-5808">
                  <label htmlFor="invoiceNumber" className="block text-sm font-medium text-gray-700" data-id="element-5809">
                    Invoice Number
                  </label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getConfidenceClass(extractedData.confidence.invoiceNumber)}`} data-id="element-5810">
                    {Math.round(extractedData.confidence.invoiceNumber * 100)}%
                  </span>
                </div>
                <input type="text" name="invoiceNumber" id="invoiceNumber" className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" value={extractedData.header.invoiceNumber} onChange={e => onFieldUpdate('header', 'invoiceNumber', e.target.value)} data-id="element-5811" />
              </div>
              <div className="sm:col-span-3" data-id="element-5812">
                <div className="flex items-center justify-between" data-id="element-5813">
                  <label htmlFor="invoiceDate" className="block text-sm font-medium text-gray-700" data-id="element-5814">
                    Invoice Date
                  </label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getConfidenceClass(extractedData.confidence.invoiceDate)}`} data-id="element-5815">
                    {Math.round(extractedData.confidence.invoiceDate * 100)}%
                  </span>
                </div>
                <input type="date" name="invoiceDate" id="invoiceDate" className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" value={extractedData.header.invoiceDate} onChange={e => onFieldUpdate('header', 'invoiceDate', e.target.value)} data-id="element-5816" />
              </div>
              <div className="sm:col-span-3" data-id="element-5817">
                <div className="flex items-center justify-between" data-id="element-5818">
                  <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700" data-id="element-5819">
                    Due Date
                  </label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getConfidenceClass(extractedData.confidence.dueDate)}`} data-id="element-5820">
                    {Math.round(extractedData.confidence.dueDate * 100)}%
                  </span>
                </div>
                <input type="date" name="dueDate" id="dueDate" className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" value={extractedData.header.dueDate} onChange={e => onFieldUpdate('header', 'dueDate', e.target.value)} data-id="element-5821" />
              </div>
              <div className="sm:col-span-3" data-id="element-5822">
                <label htmlFor="poReference" className="block text-sm font-medium text-gray-700" data-id="element-5823">
                  PO Reference
                </label>
                <input type="text" name="poReference" id="poReference" className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" value={extractedData.header.poReference} onChange={e => onFieldUpdate('header', 'poReference', e.target.value)} data-id="element-5824" />
              </div>
              <div className="sm:col-span-2" data-id="element-5825">
                <label htmlFor="currency" className="block text-sm font-medium text-gray-700" data-id="element-5826">
                  Currency
                </label>
                <select id="currency" name="currency" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={extractedData.header.currency} onChange={e => onFieldUpdate('header', 'currency', e.target.value)} data-id="element-5827">
                  <option value="AUD" data-id="element-5828">AUD</option>
                  <option value="USD" data-id="element-5829">USD</option>
                  <option value="EUR" data-id="element-5830">EUR</option>
                  <option value="GBP" data-id="element-5831">GBP</option>
                  <option value="CAD" data-id="element-5832">CAD</option>
                </select>
              </div>
              <div className="sm:col-span-2" data-id="element-5833">
                <div className="flex items-center justify-between" data-id="element-5834">
                  <label htmlFor="subtotal" className="block text-sm font-medium text-gray-700" data-id="element-5835">
                    Subtotal
                  </label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getConfidenceClass(extractedData.confidence.subtotal)}`} data-id="element-5836">
                    {Math.round(extractedData.confidence.subtotal * 100)}%
                  </span>
                </div>
                <input type="number" name="subtotal" id="subtotal" step="0.01" className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" value={extractedData.header.subtotal} onChange={e => onFieldUpdate('header', 'subtotal', parseFloat(e.target.value))} data-id="element-5837" />
              </div>
              <div className="sm:col-span-2" data-id="element-5838">
                <div className="flex items-center justify-between" data-id="element-5839">
                  <label htmlFor="tax" className="block text-sm font-medium text-gray-700" data-id="element-5840">
                    Tax
                  </label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getConfidenceClass(extractedData.confidence.tax)}`} data-id="element-5841">
                    {Math.round(extractedData.confidence.tax * 100)}%
                  </span>
                </div>
                <input type="number" name="tax" id="tax" step="0.01" className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" value={extractedData.header.tax} onChange={e => onFieldUpdate('header', 'tax', parseFloat(e.target.value))} data-id="element-5842" />
              </div>
              <div className="sm:col-span-2" data-id="element-5843">
                <div className="flex items-center justify-between" data-id="element-5844">
                  <label htmlFor="total" className="block text-sm font-medium text-gray-700" data-id="element-5845">
                    Total
                  </label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getConfidenceClass(extractedData.confidence.total)}`} data-id="element-5846">
                    {Math.round(extractedData.confidence.total * 100)}%
                  </span>
                </div>
                <input type="number" name="total" id="total" step="0.01" className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" value={extractedData.header.total} onChange={e => onFieldUpdate('header', 'total', parseFloat(e.target.value))} data-id="element-5847" />
              </div>
            </div>}
        </div>
        {/* Vendor Section */}
        <div data-id="element-5848">
          <div className="flex items-center justify-between cursor-pointer" onClick={() => toggleSection('vendor')} data-id="element-5849">
            <h4 className="text-sm font-medium text-gray-900" data-id="element-5850">
              Vendor Details
            </h4>
            {expandedSections.vendor ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-5851" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-5852" />}
          </div>
          {expandedSections.vendor && <div className="mt-3 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-5853">
              <div className="sm:col-span-3" data-id="element-5854">
                <div className="flex items-center justify-between" data-id="element-5855">
                  <label htmlFor="vendorName" className="block text-sm font-medium text-gray-700" data-id="element-5856">
                    Vendor Name
                  </label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getConfidenceClass(extractedData.confidence.vendorName)}`} data-id="element-5857">
                    {Math.round(extractedData.confidence.vendorName * 100)}%
                  </span>
                </div>
                <input type="text" name="vendorName" id="vendorName" className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" value={extractedData.vendor.name} onChange={e => onFieldUpdate('vendor', 'name', e.target.value)} data-id="element-5858" />
              </div>
              <div className="sm:col-span-3" data-id="element-5859">
                <label htmlFor="vendorId" className="block text-sm font-medium text-gray-700" data-id="element-5860">
                  Vendor ID
                </label>
                <input type="text" name="vendorId" id="vendorId" className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" value={extractedData.vendor.vendorId} onChange={e => onFieldUpdate('vendor', 'vendorId', e.target.value)} data-id="element-5861" />
              </div>
              <div className="sm:col-span-6" data-id="element-5862">
                <div className="flex items-center justify-between" data-id="element-5863">
                  <label htmlFor="vendorAddress" className="block text-sm font-medium text-gray-700" data-id="element-5864">
                    Vendor Address
                  </label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getConfidenceClass(extractedData.confidence.vendorAddress)}`} data-id="element-5865">
                    {Math.round(extractedData.confidence.vendorAddress * 100)}%
                  </span>
                </div>
                <input type="text" name="vendorAddress" id="vendorAddress" className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" value={extractedData.vendor.address} onChange={e => onFieldUpdate('vendor', 'address', e.target.value)} data-id="element-5866" />
              </div>
              <div className="sm:col-span-3" data-id="element-5867">
                <label htmlFor="vendorTaxId" className="block text-sm font-medium text-gray-700" data-id="element-5868">
                  Tax ID
                </label>
                <input type="text" name="vendorTaxId" id="vendorTaxId" className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" value={extractedData.vendor.taxId} onChange={e => onFieldUpdate('vendor', 'taxId', e.target.value)} data-id="element-5869" />
              </div>
              <div className="sm:col-span-3" data-id="element-5870">
                <label htmlFor="vendorEmail" className="block text-sm font-medium text-gray-700" data-id="element-5871">
                  Email
                </label>
                <input type="email" name="vendorEmail" id="vendorEmail" className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" value={extractedData.vendor.email} onChange={e => onFieldUpdate('vendor', 'email', e.target.value)} data-id="element-5872" />
              </div>
            </div>}
        </div>
        {/* Line Items Section */}
        <div data-id="element-5873">
          <div className="flex items-center justify-between cursor-pointer" onClick={() => toggleSection('lineItems')} data-id="element-5874">
            <h4 className="text-sm font-medium text-gray-900" data-id="element-5875">Line Items</h4>
            {expandedSections.lineItems ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-5876" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-5877" />}
          </div>
          {expandedSections.lineItems && <div className="mt-3" data-id="element-5878">
              <div className="overflow-x-auto" data-id="element-5879">
                <table className="min-w-full divide-y divide-gray-300" data-id="element-5880">
                  <thead className="bg-gray-50" data-id="element-5881">
                    <tr data-id="element-5882">
                      <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6" data-id="element-5883">
                        Description
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900" data-id="element-5884">
                        Qty
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900" data-id="element-5885">
                        Unit Price
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900" data-id="element-5886">
                        Tax
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900" data-id="element-5887">
                        Total
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900" data-id="element-5888">
                        Confidence
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white" data-id="element-5889">
                    {extractedData.lineItems.map((item, index) => <tr key={item.id} data-id="element-5890">
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6" data-id="element-5891">
                          <input type="text" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" value={item.description} onChange={e => onLineItemUpdate(index, 'description', e.target.value)} data-id="element-5892" />
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500" data-id="element-5893">
                          <input type="number" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" value={item.quantity} onChange={e => onLineItemUpdate(index, 'quantity', parseInt(e.target.value))} data-id="element-5894" />
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500" data-id="element-5895">
                          <input type="number" step="0.01" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" value={item.unitPrice} onChange={e => onLineItemUpdate(index, 'unitPrice', parseFloat(e.target.value))} data-id="element-5896" />
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500" data-id="element-5897">
                          <input type="number" step="0.01" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" value={item.tax} onChange={e => onLineItemUpdate(index, 'tax', parseFloat(e.target.value))} data-id="element-5898" />
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500" data-id="element-5899">
                          <input type="number" step="0.01" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" value={item.total} onChange={e => onLineItemUpdate(index, 'total', parseFloat(e.target.value))} data-id="element-5900" />
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-center" data-id="element-5901">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getConfidenceClass(item.confidence)}`} data-id="element-5902">
                            {Math.round(item.confidence * 100)}%
                          </span>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>}
        </div>
        {/* GL Coding Section */}
        <div data-id="element-5903">
          <div className="flex items-center justify-between cursor-pointer" onClick={() => toggleSection('glCoding')} data-id="element-5904">
            <h4 className="text-sm font-medium text-gray-900" data-id="element-5905">GL Coding</h4>
            {expandedSections.glCoding ? <ChevronUpIcon className="h-5 w-5 text-gray-500" data-id="element-5906" /> : <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-5907" />}
          </div>
          {expandedSections.glCoding && <div className="mt-3 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-5908">
              <div className="sm:col-span-3" data-id="element-5909">
                <label htmlFor="department" className="block text-sm font-medium text-gray-700" data-id="element-5910">
                  Department
                </label>
                <input type="text" name="department" id="department" className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" value={extractedData.glCoding.department} onChange={e => onFieldUpdate('glCoding', 'department', e.target.value)} data-id="element-5911" />
              </div>
              <div className="sm:col-span-3" data-id="element-5912">
                <label htmlFor="account" className="block text-sm font-medium text-gray-700" data-id="element-5913">
                  Account
                </label>
                <input type="text" name="account" id="account" className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" value={extractedData.glCoding.account} onChange={e => onFieldUpdate('glCoding', 'account', e.target.value)} data-id="element-5914" />
              </div>
              <div className="sm:col-span-3" data-id="element-5915">
                <label htmlFor="project" className="block text-sm font-medium text-gray-700" data-id="element-5916">
                  Project
                </label>
                <input type="text" name="project" id="project" className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" value={extractedData.glCoding.project} onChange={e => onFieldUpdate('glCoding', 'project', e.target.value)} data-id="element-5917" />
              </div>
              <div className="sm:col-span-3" data-id="element-5918">
                <label htmlFor="costCenter" className="block text-sm font-medium text-gray-700" data-id="element-5919">
                  Cost Center
                </label>
                <input type="text" name="costCenter" id="costCenter" className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" value={extractedData.glCoding.costCenter} onChange={e => onFieldUpdate('glCoding', 'costCenter', e.target.value)} data-id="element-5920" />
              </div>
            </div>}
        </div>
      </div>
    </div>;
}